import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { SidebarViewState } from 'apps/aiAutomation/components/Layout/lib/types'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { AiAutomationEngagementGoalActions } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationKnowledgeActions } from 'apps/aiAutomation/store/knowledge'
import {
  getViewsPayloadDefaults,
  getViewsStateDefaults,
  setViewPayload,
} from 'apps/aiAutomation/store/layout/lib'
import {
  AiAutomationLayoutInitialState,
  AiAutomationLayoutState,
  ViewPayload,
} from 'apps/aiAutomation/store/layout/types'

const { reducer, actions } = createSlice({
  name: 'aiAutomationLayout',
  initialState: AiAutomationLayoutInitialState,
  reducers: {
    sidebarOpened(
      state: AiAutomationLayoutState,
      action: PayloadAction<ViewPayload & { isSidebarExpanded?: boolean }>,
    ) {
      state.isSidebarOpen = true

      const viewState = getViewsStateDefaults()

      viewState[action.payload.view] = 'open'

      state.viewState = viewState

      setViewPayload(state, action.payload)

      state.isSidebarExpanded = action.payload.isSidebarExpanded ?? false
    },

    sidebarClosed(state) {
      if (state.showConfirm) {
        state.isConfirmOpen = true
      } else {
        state.isSidebarOpen = false
      }
    },

    viewOpened(state, action: PayloadAction<ViewPayload & { isSidebarExpanded?: boolean }>) {
      const viewStateEntries = Object.entries(state.viewState) as Array<
        [SidebarViewType, SidebarViewState]
      >

      viewStateEntries.forEach(([view, viewState]) => {
        if (view === action.payload.view) {
          state.viewState[action.payload.view] = 'open'
        } else {
          state.viewState[view] = viewState === 'open' ? 'hidden' : viewState
        }
      })

      setViewPayload(state, action.payload)

      state.showConfirm = false
      state.isSidebarExpanded = action.payload.isSidebarExpanded ?? false
    },

    currentViewReplaced(
      state,
      action: PayloadAction<ViewPayload & { isSidebarExpanded?: boolean }>,
    ) {
      const viewStateEntries = Object.entries(state.viewState) as Array<
        [SidebarViewType, SidebarViewState]
      >

      viewStateEntries.forEach(([view, viewState]) => {
        if (view === action.payload.view) {
          state.viewState[action.payload.view] = 'open'
        } else {
          state.viewState[view] = viewState === 'open' ? 'closed' : viewState
        }
      })

      setViewPayload(state, action.payload)

      state.showConfirm = false
      state.isSidebarExpanded = action.payload.isSidebarExpanded ?? false
    },

    viewPayloadUpdated(state, action: PayloadAction<ViewPayload>) {
      setViewPayload(state, action.payload)
    },

    viewStateReset(state) {
      state.viewState = getViewsStateDefaults()
      state.viewPayload = getViewsPayloadDefaults()
      state.isSidebarExpanded = false
    },

    interactiveViewUpdated(state, action: PayloadAction<SidebarViewType | null>) {
      state.interactiveView = action.payload
    },

    showConfirmChanged(state, action: PayloadAction<boolean>) {
      state.showConfirm = action.payload
    },

    confirmClosed(state, action: PayloadAction<{ closeSidebar: boolean }>) {
      state.isConfirmOpen = false

      if (action.payload.closeSidebar) {
        state.isSidebarOpen = false
        state.showConfirm = false
      }
    },

    expandSidebarChanged(state, action: PayloadAction<boolean>) {
      state.isSidebarExpanded = action.payload
    },
  },

  extraReducers(builder) {
    builder.addCase(AiAutomationKnowledgeActions.entryDeleted, (state, action) => {
      const editorViewState = state.viewState[SidebarViewType.KNOWLEDGE_EDITOR]
      const editorViewPayload = state.viewPayload[SidebarViewType.KNOWLEDGE_EDITOR]

      if (
        state.isSidebarOpen &&
        editorViewState !== 'closed' &&
        editorViewPayload?.entryId === action.payload.id
      ) {
        state.isSidebarOpen = false
        state.showConfirm = false
      }
    })

    builder.addCase(AiAutomationEngagementGoalActions.goalDeleted, (state, action) => {
      const editorViewState = state.viewState[SidebarViewType.ENGAGEMENT_GOAL_EDITOR]
      const editorViewPayload = state.viewPayload[SidebarViewType.ENGAGEMENT_GOAL_EDITOR]

      if (
        state.isSidebarOpen &&
        editorViewState !== 'closed' &&
        editorViewPayload?.goalId === action.payload.id
      ) {
        state.isSidebarOpen = false
        state.showConfirm = false
      }
    })
  },
})

export const AiAutomationLayoutActions = {
  sidebarOpened: actions.sidebarOpened,
  sidebarClosed: actions.sidebarClosed,

  viewOpened: actions.viewOpened,
  currentViewReplaced: actions.currentViewReplaced,
  viewStateReset: actions.viewStateReset,
  viewPayloadUpdated: actions.viewPayloadUpdated,

  interactiveViewUpdated: actions.interactiveViewUpdated,
  showConfirmChanged: actions.showConfirmChanged,
  confirmClosed: actions.confirmClosed,

  expandSidebarChanged: actions.expandSidebarChanged,
}

const getState = (state: RootState) => state.aiAutomation.layout

export const AiAutomationLayoutSelectors = {
  getIsSidebarOpen: (state: RootState) => getState(state).isSidebarOpen,
  getIsConfirmOpen: (state: RootState) => getState(state).isConfirmOpen,
  getIsSidebarExpanded: (state: RootState) => getState(state).isSidebarExpanded,

  getViewState: (state: RootState, view: SidebarViewType) => getState(state).viewState[view],
  getViewPayload: <View extends SidebarViewType>(state: RootState, view: View) =>
    getState(state).viewPayload[view],

  getInteractiveView: (state: RootState) => getState(state).interactiveView,
}

export const AiAutomationLayoutReducer = reducer
